import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, CheckCircle2, Circle, Download, Linkedin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const NAMES = { CSCE: "CSCE", CSP: "CSP", CSL: "CSL", CCSO: "CCSO", EIP: "Executive Integration Portfolio" };

export const CreditTracker = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/me/credits").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const share = async () => {
    const w = window.open("about:blank", "_blank");
    try {
      const { data } = await api.post("/me/credits/share");
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(data.share_url)}`;
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) { w?.close(); toast.error("Could not create your share link"); }
  };
  const pct = Math.round((d.earned / d.total) * 100);
  return (
    <section className="lg:col-span-12 card-warm p-5 sm:p-6" data-testid="credit-tracker">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl inline-flex items-center gap-2"><GraduationCap size={18} aria-hidden="true"/> My GCSB Academic Credits</h2>
          <p className="text-sm text-zinc-600 mt-1">Your progress towards the complete PGDCSL.</p>
        </div>
        <div className="sm:text-right">
          <div className="font-display text-3xl text-[var(--navy)]" data-testid="credit-tracker-total">{d.earned}<span className="text-zinc-400 text-xl"> / {d.total}</span></div>
          <div className="text-xs text-zinc-500">GCSB Academic Credits</div>
        </div>
      </div>
      <div className="mt-4 h-3 rounded-full bg-[var(--muted-surface)] overflow-hidden" role="progressbar" aria-valuenow={d.earned} aria-valuemin={0} aria-valuemax={d.total} aria-label="GCSB Academic Credits earned">
        <div className="h-full bg-[var(--gold-deep)] transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid="credit-tracker-bar"/>
      </div>
      <ul className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2">
        {d.components.map((c) => (
          <li key={c.code} className={`rounded-xl border px-3 py-2 text-xs ${c.earned ? "border-emerald-300 bg-emerald-50" : "border-[var(--border-warm)] bg-white"}`} data-testid={`credit-${c.code}`}>
            <div className="flex items-center gap-1.5 font-semibold text-[var(--navy)]">
              {c.earned ? <CheckCircle2 size={13} className="text-emerald-600" aria-hidden="true"/> : <Circle size={13} className="text-zinc-300" aria-hidden="true"/>}
              {NAMES[c.code]}
            </div>
            <div className="text-zinc-600 mt-0.5">{c.credits} credits · {c.earned ? "Earned" : "Not yet"}</div>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/me/credits/statement.pdf`} className="chip text-xs inline-flex items-center gap-1.5" data-testid="credit-statement-download">
          <Download size={13} aria-hidden="true"/> Download credits statement (PDF)
        </a>
        {d.earned > 0 && (
          <button type="button" onClick={share} className="chip text-xs inline-flex items-center gap-1.5" data-testid="credit-share-linkedin">
            <Linkedin size={13} aria-hidden="true"/> Share on LinkedIn
          </button>
        )}
        {d.earned < d.total && (
          <Link to="/education" className="text-sm font-semibold text-[var(--terracotta)] hover:underline" data-testid="credit-tracker-next">See the full pathway →</Link>
        )}
      </div>
    </section>
  );
};
